import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  isLoaded as isLoadedProducts,
  Plan,
  PlanModel,
  UnassignedPlanModel,
} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type ICRTNProps = {
  productType: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  icrUnits: StringUnion;
  ropRider: boolean;
  cwa: StringUnion;
  groupTrust: boolean;
  section125: boolean;
  replacement: boolean;
  originalAge: number;
  reRate: boolean;
  policyNumber: PolicyNumber;
  productList: PlanModel;
  overRideBillForm: boolean;
};

export class InvalidOrdinaryICRTN {
  public readonly key = 'InvalidOrdinaryICRTN';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly icrUnits: StringUnion,
    public readonly ropRider: boolean,
    public readonly cwa: StringUnion,
    public readonly groupTrust: boolean,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly originalAge: number,
    public readonly reRate: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly productList: PlanModel,
    public readonly overRideBillForm: boolean
  ) {}
}

export class ValidOrdinaryICRTN {
  public static createApp({
    productType,
    planType,
    premium,
    calculatedPremium,
    icrUnits,
    ropRider,
    cwa,
    groupTrust,
    section125,
    replacement,
    originalAge,
    reRate,
    policyNumber,
    productList,
    overRideBillForm,
  }: ICRTNProps) {
    return this.create(
      productType,
      planType,
      premium,
      calculatedPremium,
      icrUnits,
      ropRider,
      cwa,
      groupTrust,
      section125,
      replacement,
      originalAge,
      reRate,
      policyNumber,
      productList,
      overRideBillForm
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    icrUnits: StringUnion,
    ropRider: boolean,
    cwa: StringUnion,
    groupTrust: boolean,
    section125: boolean,
    replacement: boolean,
    originalAge: number,
    reRate: boolean,
    policyNumber: PolicyNumber,
    productList: PlanModel,
    overRideBillForm: boolean
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      groupTrust &&
      section125 &&
      replacement &&
      originalAge &&
      reRate &&
      policyNumber &&
      isLoadedProducts(productList)
      ? new ValidOrdinaryICRTN(
          productType,
          planType,
          premium,
          calculatedPremium,
          icrUnits,
          ropRider,
          cwa,
          groupTrust,
          section125,
          replacement,
          originalAge,
          reRate,
          policyNumber,
          productList,
          overRideBillForm
        )
      : new InvalidOrdinaryICRTN(
          productType,
          planType,
          premium,
          calculatedPremium,
          icrUnits,
          ropRider,
          cwa,
          groupTrust,
          section125,
          replacement,
          originalAge,
          reRate,
          policyNumber,
          productList,
          overRideBillForm
        );
  }

  public readonly key = 'ValidOrdinaryICRTN';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly icrUnits: StringUnion,
    public readonly ropRider: boolean,
    public readonly cwa: StringUnion,
    public readonly groupTrust: boolean,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly originalAge: number,
    public readonly reRate: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly productList: Loaded<Plan[]>,
    public readonly overRideBillForm: boolean
  ) {}
}

export type OrdinaryICRTN = InvalidOrdinaryICRTN | ValidOrdinaryICRTN;

export type OrdinaryICRTNOptions<TReturn> = ToPartialMatchOptions<OrdinaryICRTN, 'key', TReturn>;

export const matchOrdinaryICRTN = <TReturn>(options: OrdinaryICRTNOptions<TReturn>) => (
  app: OrdinaryICRTN
): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryICRTN':
      return options.InvalidOrdinaryICRTN ? options.InvalidOrdinaryICRTN(app) : options.default(app);
    case 'ValidOrdinaryICRTN':
      return options.ValidOrdinaryICRTN ? options.ValidOrdinaryICRTN(app) : options.default(app);
  }
};

export const EmptyOrdinaryICRTN = new InvalidOrdinaryICRTN(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  false,
  false,
  false,
  0,
  false,
  EmptyPolicyNumber,
  new UnassignedPlanModel(),
  false
);

export const changeProductType = (productType: string) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({
    ...app,
    productType: NonEmptyString.create(productType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
    reRate: false,
    originalAge: 0,
  });

export const changePlanType = (planType: string) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({
    ...app,
    planType: NonEmptyString.create(planType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
    reRate: false,
    originalAge: 0,
  });

export const changeICRUnits = (icrUnits: string) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, icrUnits: NonEmptyString.create(icrUnits)});

export const changeROPRider = (ropRider: boolean) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, ropRider});

export const changePremium = (premium: string) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeGroupTrust = (groupTrust: boolean) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, groupTrust});

export const changeSection125 = (section125: boolean) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({
    ...app,
    replacement,
    originalAge: 0,
    policyNumber: app.replacement && !app.reRate ? ValidPolicyNumber.create(new EmptyString()) : app.policyNumber,
  });

export const changeOriginalAge = (originalAge: number) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, originalAge});

export const changeReRate = (reRate: boolean) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({
    ...app,
    reRate,
    policyNumber: app.reRate && !app.replacement ? ValidPolicyNumber.create(new EmptyString()) : app.policyNumber,
  });

export const changePolicyNumber = (policyNumber: StringUnion) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeProductList = (products: PlanModel) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, productList: products});

export const changeOverrideBillForm = (overRideBillForm: boolean) => (app: OrdinaryICRTN) =>
  ValidOrdinaryICRTN.createApp({...app, overRideBillForm});
